import React, { useMemo } from "react";
import uuid from "react-uuid";
import { CategoryTheme, EMPTY_ARRAY } from "../misc";
import { getTraceColors } from "../../themes";
import { MultiSingleHorizontalThreeColumnBar } from "./MultiSingleHorizontalThreeColumnBar";
import styles from "./HorizontalChartWithData.module.scss";
import { formatNumber } from "../../utils";

export interface HorizontalChartWithDataProps {
    annotationSuffix?: string;
    annotationPrefix?: string;

    data?: { value: number; label: string }[];
    theme?: CategoryTheme;
    className?: string;
    queryHeightWatchingElements?: () => { watching: HTMLElement[]; update: HTMLElement[] };
    enableLabelWidthWatching?: boolean;
    maxValue?: number;
    queryLabelWidthWatchingElements?: () => {
        watching: HTMLElement[];
        update: HTMLElement[];
    };
}

export const HorizontalChartWithData: React.FC<HorizontalChartWithDataProps> = ({
    data,
    annotationPrefix,
    annotationSuffix,
    className,
    maxValue,
    theme,
    queryHeightWatchingElements,
    enableLabelWidthWatching,
    queryLabelWidthWatchingElements
}) => {
    const hasData = useMemo(() => {
        return data?.find(d => d?.value != null) != null;
    }, [data]);
    const labels = useMemo(() => {
        return data?.map(d => d?.label);
    }, [data]);

    const values = useMemo(() => {
        return data?.map(d => d?.value ?? 0);
    }, [data]);

    const id = useMemo(() => {
        return uuid();
    }, []);

    if (!hasData) return null;

    return (
        <MultiSingleHorizontalThreeColumnBar
            maxValue={maxValue}
            className={`${styles.horizontalChartWithData} ${className ?? ""}`}
            id={id}
            labels={labels}
            values={values}
            backgroundColors={getTraceColors(theme, values?.length, labels)}
            highlights={values.map(
                value =>
                    `${annotationPrefix == null ? "" : `${annotationPrefix} `}${formatNumber(value)}${
                        annotationSuffix == null ? "" : ` ${annotationSuffix}`
                    }`
            )}
            afterHighlights={EMPTY_ARRAY}
            seriesName={""}
            queryHeightWatchingElements={queryHeightWatchingElements}
            enableLabelWidthWatching={enableLabelWidthWatching}
            queryLabelWidthWatchingElements={queryLabelWidthWatchingElements}
        />
    );
};

export const HorizontalChartWithDataDemo: React.FC<{}> = () => {
    const labels = ["Permanent Water Conservation Measures", "Level 1", "Level 2"];
    const values = [365, 321, 45];

    const data = useMemo(() => {
        return labels.map((label, index) => {
            return {
                label,
                value: values?.[index]
            };
        });
    }, [labels, values]);

    return (
        <HorizontalChartWithData
            data={data}
            annotationPrefix={null}
            annotationSuffix={"days"}
            theme={"water-security"}
        />
    );
};
